
import { useTranslation } from "react-i18next"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Star, Eye } from "lucide-react"
import type { Review } from "@/api/reviews"
import { DataTablePagination } from "@/components/ui/data-table-pagination"
import { TableEmptyRow, TableLoadingRows } from "@/components/shared/table-states"

interface ReviewsListTableProps {
  reviews: Review[]
  isLoading: boolean
  onViewDetails: (reviewId: string) => void
  currentPage: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void
}

export function ReviewsListTable({
  reviews,
  isLoading,
  onViewDetails,
  currentPage,
  totalPages,
  total,
  onPageChange,
}: ReviewsListTableProps) {
  const { t } = useTranslation()

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-muted-foreground">
          {rating}/5
        </span>
      </div>
    )
  }

  const getClassName = (review: Review) => {
    if (review.classId) {
      return review.classId.label || review.classId.name
    }
    return '-'
  }

  const getInstructorName = (review: Review) => {
    if (review.staffId) {
      return `${review.staffId.name} ${review.staffId.surname}`
    }
    return '-'
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('reviews.date')}</TableHead>
            <TableHead>{t('reviews.member')}</TableHead>
            <TableHead>{t('reviews.class')}</TableHead>
            <TableHead>{t('reviews.instructor')}</TableHead>
            <TableHead>{t('reviews.rating')}</TableHead>
            <TableHead>{t('reviews.feedback')}</TableHead>
            <TableHead>{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoadingRows colSpan={7} />
          ) : !reviews || reviews.length === 0 ? (
            <TableEmptyRow colSpan={7} message={t('reviews.noReviews')} />
          ) : (
            reviews.map((review) => (
              <TableRow key={review._id}>
                <TableCell className="whitespace-nowrap">
                  {new Date(review.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span>{review.userName}</span>
                  </div>
                </TableCell>
                <TableCell>{getClassName(review)}</TableCell>
                <TableCell>{getInstructorName(review)}</TableCell>
                <TableCell>{renderStars(review.rating)}</TableCell>
                <TableCell>
                  <div className="max-w-xs truncate">
                    {review.feedback || '-'}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(review._id)}
                    className="gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {t('reviews.viewDetails')}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!isLoading && (
        <DataTablePagination
          page={currentPage}
          pageCount={totalPages}
          total={total}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
